import { ReactElement, useState } from 'react'
import { useMatch } from 'react-router-dom'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { Button, MenuItem, Select, Stack } from '@mui/material'
import { AIAccessResourceType } from '@api/fastAPI.schemas'

interface Props {
  onGrantProject: (projectGuid: string) => void
  onGrantOrgCapability: (resourceType: 'system_manager' | 'new_projects') => void
  isBusy: boolean
}

/**
 * Pick what to release to the AI, then confirm with "Freigeben". The
 * Systemmanager and Neue-Projekte grants are always offered (each its own
 * grant); the current project additionally whenever one is open, whichever
 * page of it is active.
 */
const GrantSelector = ({ onGrantProject, onGrantOrgCapability, isBusy }: Props): ReactElement => {
  const editorMatch = useMatch('/editor/:projectId/*')
  const projectId = editorMatch?.params.projectId

  const [target, setTarget] = useState<AIAccessResourceType>(
    projectId ? 'project' : 'system_manager',
  )

  // Selection survives from a project context, the project itself may not
  // (popover kept open across navigation): fall back to the Systemmanager.
  const effectiveTarget: AIAccessResourceType =
    target === 'project' && !projectId ? 'system_manager' : target

  const handleGrant = () => {
    if (effectiveTarget === 'project' && projectId) {
      onGrantProject(projectId)
    } else if (effectiveTarget !== 'project') {
      onGrantOrgCapability(effectiveTarget)
    }
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Select<AIAccessResourceType>
        size="small"
        value={effectiveTarget}
        onChange={event => setTarget(event.target.value as AIAccessResourceType)}
        sx={{ flex: 1 }}
        data-cy="ai-access-grant-select"
      >
        {projectId && (
          <MenuItem value="project" data-cy="ai-access-grant-option-project">
            Aktuelles Projekt
          </MenuItem>
        )}
        <MenuItem value="system_manager" data-cy="ai-access-grant-option-system-manager">
          Systemmanager
        </MenuItem>
        <MenuItem value="new_projects" data-cy="ai-access-grant-option-new-projects">
          Neue Projekte
        </MenuItem>
      </Select>
      <Button
        size="small"
        variant="contained"
        startIcon={<AutoAwesomeOutlinedIcon />}
        onClick={handleGrant}
        disabled={isBusy}
        data-cy="ai-access-grant-submit"
      >
        Freigeben
      </Button>
    </Stack>
  )
}

export default GrantSelector
